/// <amd-module name="Build/Run/Components/RunSummary" />
import { BuildReason } from "Build/Client/Contracts/Build";
import { ArtifactsData, ChangesData, RunDetailsData } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { BranchLink } from "Build/Common/Library/Components/BranchLink";
import { BuildPersona } from "Build/Common/Library/Components/BuildPersona";
import { CommitLink } from "Build/Common/Library/Components/CommitLink";
import { RepoLink } from "Build/Common/Library/Components/RepoLink";
import { getAvatarUrl } from "Build/Common/Library/Identity";
import { ArtifactsLink } from "Build/Run/Components/ArtifactsLink";
import { ChangesLink } from "Build/Run/Components/ChangesLink";
import { CoverageResultsLink } from "Build/Run/Components/CoverageResultsLink";
import { RunSummaryChangesLink } from "Build/Run/Components/RunSummaryChangesLinkComponent";
import { RunSummaryWorkItemsLink } from "Build/Run/Components/RunSummaryWorkItemsLinkComponent";
import { TestResultsLink } from "Build/Run/Components/TestResultsLink";
import { WorkItemsLink } from "Build/Run/Components/WorkItemsLink";
import * as Resources from "Build/Run/Resources";
import { IRunDetailsService } from "Build/Run/RunDetailsService.types";
import * as React from "react";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { onClickFPS } from "VSS/Platform/FPS";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Ago } from "VSSUI/Ago";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Duration } from "VSSUI/Duration";
import { TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Image } from "VSSUI/Image";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize } from "VSSUI/Pill";
import { IBreakPoint, ResponsiveViewport } from "VSSUI/ResponsiveViewport";
import { css, getSafeId } from "VSSUI/Util";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ScreenSizeConditional, ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { getRepositoryResourceItems } from "./SourcesCard";



export interface IRunSummaryProps extends IVssComponentProperties {
    testsTabUrl: string;
}

export interface IRunSummaryState {
    run: RunDetailsData;
    associatedChanges: IReadonlyObservableValue<ChangesData>;
    artifacts: IReadonlyObservableValue<ArtifactsData>;
    traceabilityChangesCount: IReadonlyObservableValue<number>;
    traceabilityWorkItemsCount: IReadonlyObservableValue<number>;
    fallbackTraceabilityFlag: IReadonlyObservableValue<boolean>;
}

export interface ISummaryColumnProps {
    compact: boolean;
    label: string;
    labelId?: string;
    count?: number;
}

export namespace TriggerInfo {
    export const artifactType = "artifactType";
    export const projectId = "projectId";
    export const source = "source";
    export const pipelineId = "pipelineId";
    export const version = "version";
    export const alias = "alias";
    export const tag = "tag";
    export const pipelineTriggerType = "pipelineTriggerType";
    export const scheduleName = "scheduleName";
}

export namespace PipelineTriggerType {
    export const BuildResourceCompletion = "BuildResourceCompletion";
    export const ContainerImage = "ContainerImage";
    export const ContinuousIntegration = "ContinuousIntegration";
    export const PullRequest = "PullRequest";
    export const PipelineCompletion = "PipelineCompletion";
    export const WebhookTriggeredEvent = "WebhookTriggeredEvent";
}

interface IRunItemSummaryBreakpoint extends IBreakPoint {
    compact: boolean;
}

const SummaryColumn: React.FunctionComponent<ISummaryColumnProps> = props => {

    return (
        <div className={css("flex-grow", props.compact ? "flex-row flex-self-start summary-column-compact" : "flex-column summary-column")}>
            <div className="flex-row">
                <div className={css("secondary-text summary-line-non-link", props.compact && "summary-line-compact")} id={getSafeId(props.labelId)}>{props.label}</div>
                { props.count && props.count > 1 && <Pill className={"margin-horizontal-4"} size={PillSize.compact} >{props.count}</Pill>}
            </div>
            <div className={css("summary-line flex-column flex-grow")}>
                {props.children}
            </div>
        </div>
    );
};

export class RunSummary extends VssComponent<IRunSummaryProps, IRunSummaryState> {
    private _service: IRunDetailsService;
    private _observable: IReadonlyObservableValue<RunDetailsData>;
    private _showSummary = new ObservableValue(false);

    constructor(props: IRunSummaryProps, context: IVssComponentContext) {
        super(props, context);

        this._service = this.context.pageContext.getService<IRunDetailsService>("IRunDetailsService");
        this._observable = this._service.data;

        this.state = {
            run: this._service.data.value,
            associatedChanges: this._service.associatedChanges,
            artifacts: this._service.artifacts,            
            traceabilityWorkItemsCount: this._service.traceabilityWorkItemsCount,
            traceabilityChangesCount: this._service.traceabilityChangesCount,
            fallbackTraceabilityFlag: this._service.fallbackTraceabilityFlag
        };
    }

    public render() {

        return (
            <ResponsiveViewport<IRunItemSummaryBreakpoint>
                breakPoints={[{
                    className: "summary-view-compact",
                    maxWidth: 372 + 112 * 5 /*width of summary info + 5 columns at min-width 112. This should be kept in sync with variables in RunView.scss*/,
                    compact: true
                }]}
                onRenderContent={this._renderCard}
            />
        );
    }

    public componentDidMount() {
        this._observable.subscribe(this._onObservableChanged);
    }

    public componentWillUnmount() {
        this._observable.unsubscribe(this._onObservableChanged);
    }

    private _onObservableChanged = (data: RunDetailsData) => {
        this.setState({
            run: data
        })
    }

    private _renderCard = (activeBreakpoints: IRunItemSummaryBreakpoint[]) => {

        const compactCard = activeBreakpoints.some(breakpoint => breakpoint.compact);

        const run = this.state.run;
        const multiRepo = run.repositoryResources && run.repositoryResources.length > 1;
        return <div role="region" aria-label={Resources.SummaryTitle}><Card className="flex-grow summary-card" contentProps={{ className: "summary-card-content-container" }} titleProps={{ size: TitleSize.Medium }}>
            {
                !!run &&
                <div className={css("flex-column summary-card-content flex-grow", compactCard && "scroll-hidden")}>
                    <div className="flex-row flex-center summary-header  padding-bottom-16">
                        <div className="flex-grow text-ellipsis">
                            {this._getTriggerElement()}
                        </div>
                        <ScreenSizeConditional condition={size => size === ScreenSize.xsmall}>
                            <Button iconProps={{ iconName: "ChevronUnfold10" }} subtle={true} onClick={this._onShowSummaryClick} />
                        </ScreenSizeConditional>
                    </div>
                    <div className={css("flex-center flex-grow body-m", compactCard ? "flex-column summary-view-compact" : "flex-row summary-view")}>
                        <SummaryColumn 
                            compact={compactCard} 
                            label={multiRepo ? Resources.RepositoriesHeader : Resources.RepositoryVersionHeader} 
                            labelId="repo-label" 
                            count={multiRepo && run.repositoryResources.length || undefined}>
                                {this._renderRepoColumn(run)}
                        </SummaryColumn>
                        <ScreenSizeObserver>
                            {(screenProps: { screenSize: ScreenSize }) => {
                                const summaryItems = (
                                    <>
                                        <SummaryColumn compact={compactCard} label={Resources.TimeElapsedHeader} labelId="duration-label">
                                            {
                                                <div className="flex-row summary-line summary-line-non-link flex-center">
                                                    <Icon iconName="Calendar" className="icon-margin summary-card-icon" />
                                                    <Ago date={run.queueTime} format={AgoFormat.Extended} />
                                                </div>
                                            }
                                            {
                                                !!run.startTime &&
                                                <div aria-describedby="duration-label" className="summary-line summary-line-non-link flex-center flex-row" >
                                                    <Icon iconName="Clock" className="icon-margin summary-card-icon" />
                                                    <Duration startDate={run.startTime} endDate={run.finishTime} />
                                                </div>
                                            }
                                            {
                                                !run.startTime &&
                                                <div className="summary-line summary-line-non-link flex-row flex-center"><span aria-label={Resources.None}></span><span aria-hidden={true}>-</span></div>
                                            }
                                        </SummaryColumn>
                                        <SummaryColumn compact={compactCard} label={Resources.RelatedHeader} labelId="work-items-label">
                                            { this._getWorkItemsLinkComponent(run) }
                                            <ArtifactsLink run={this.state.run} artifactsData={this.state.artifacts} linkDescribedBy="artifacts-label" />                             
                                        </SummaryColumn>
                                        <SummaryColumn compact={compactCard} label={Resources.TestsHeader} labelId="tests-label">
                                            <TestResultsLink linkDescribedBy="tests-label" testsTabUrl={this.props.testsTabUrl} />
                                            <CoverageResultsLink linkDescribedBy="coverage-label" coverageTabUrl={this.props.coverageTabUrl}/>
                                        </SummaryColumn>
                                    </>
                                );

                                return (
                                    <Observer showSummary={this._showSummary}>
                                        {(props: { showSummary: boolean }) => {
                                            if (props.showSummary || screenProps.screenSize > ScreenSize.xsmall) {
                                                // We always show summary for non-mobile screen no matter the value of showSummary is
                                                return summaryItems;
                                            }
                                            else {
                                                // Don't display summary items for mobile
                                                return null;
                                            }
                                        }}
                                    </Observer>
                                );
                            }}
                        </ScreenSizeObserver>
                    </div>
                </div>
            }
        </Card>
        </div>
    }

    private _renderRepoColumn(run: RunDetailsData): JSX.Element {
        const repositories = getRepositoryResourceItems(run, this.context);
        if (repositories.length > 1) {
            const numExtraRepositories = repositories.length - 1;

            return <>
                <div className="flex-row summary-info flex-center">
                    <RepoLink 
                        sourceRepositoryId={run.sourceRepositoryId} 
                        sourceRepositoryName={run.sourceRepositoryName} 
                        sourceRepositoryType={run.sourceRepositoryType} 
                        sourceRepositoryUrl={run.sourceRepositoryUrl} />
                    , +{numExtraRepositories}
                </div>
                <div className="flex-row summary-info flex-center">
                    <Link className="sourcecard-link" href="#SourceCard">
                        {Resources.SourceCardLink}
                    </Link>
                </div>
            </>;
        } else {
            return <>
                <div className="flex-row summary-info flex-center">
                    <RepoLink 
                        sourceRepositoryId={run.sourceRepositoryId} 
                        sourceRepositoryName={run.sourceRepositoryName} 
                        sourceRepositoryType={run.sourceRepositoryType} 
                        sourceRepositoryUrl={run.sourceRepositoryUrl} />
                </div>
                <div className="flex-row summary-info flex-center">
                    <BranchLink
                        excludeTabStop={false}
                        repositoryType={run.sourceRepositoryType}
                        repositoryName={run.sourceRepositoryName}
                        repositoryId={run.sourceRepositoryId}
                        repositoryUrl={run.sourceRepositoryUrl}
                        branch={run.sourceBranch}
                        monospace={false} />
                    <CommitLink
                        excludeTabStop={false}
                        repositoryName={run.sourceRepositoryName}
                        repositoryId={run.sourceRepositoryId}
                        repositoryType={run.sourceRepositoryType}
                        commitId={run.sourceVersion}
                        monospace={false} />
                </div>
            </>
        }
    }

    private _getChangesLinkComponent(run: RunDetailsData): JSX.Element {

        return (
            <Observer fallbackTraceabilityFlag = {this.state.fallbackTraceabilityFlag}>
            {
                (observerProps: { fallbackTraceabilityFlag: boolean }) => {

                    if (this._isRunSummaryTraceabilityEnabled && !observerProps.fallbackTraceabilityFlag) {
                        return (
                            <RunSummaryChangesLink
                                traceabilityChangesCount={this.state.traceabilityChangesCount}
                                linkDescribedBy="changes-label"
                                sourceType={run.sourceRepositoryType}
                                currentRunId={run.id}
                            />
                        );
                    }
                    else {
                        return (
                            <ChangesLink
                                linkDescribedBy="changes-label"
                                associatedChangesData={this.state.associatedChanges}
                                branch={run.sourceBranch}
                                repositoryName={run.sourceRepositoryName}
                                repositoryId={run.sourceRepositoryId}
                                repositoryUrl={run.sourceRepositoryUrl}
                                sourceType={run.sourceRepositoryType}
                                reason={run.reason} />
                        );
                    }
                }
            }
            </Observer>
        );        
    }

    private get _isRunSummaryTraceabilityEnabled(): boolean {
        return this._service && this._service.isRunSummaryTraceabilityEnabled;
    }

    private _getWorkItemsLinkComponent(run: RunDetailsData): JSX.Element {
        return (
            <Observer fallbackTraceabilityFlag = {this.state.fallbackTraceabilityFlag}>
            {
                (observerProps: { fallbackTraceabilityFlag: boolean }) => {

                    if (this._isRunSummaryTraceabilityEnabled && !observerProps.fallbackTraceabilityFlag) {
                        return (
                            <RunSummaryWorkItemsLink
                                traceabilityWorkItemsCount = {this.state.traceabilityWorkItemsCount}
                                linkDescribedBy = "work-items-label"
                                currentRunId = {run.id}/>
                        );
                    }
                    else {
                        return (
                            <WorkItemsLink linkDescribedBy="work-items-label" associatedChangesData={this.state.associatedChanges} sourceType={this.state.run.sourceRepositoryType} />
                        );
                    }
                }
            }
            </Observer>
        );
        
    }

    private _getTriggerElement(): JSX.Element {
        const run = this.state.run;
        if (run) {
            const user = run.requestedFor && run.requestedFor.displayName;
            const imageUrl = this._getAvatarUrl(run.requestedFor);
            const scheduleName = run.triggerInfo[TriggerInfo.scheduleName];

            const flexCenterClass = "flex-row flex-center";
            const byElement = user && (
                <span className={flexCenterClass}>
                    &nbsp;{Resources.By}&nbsp;
                    <BuildPersona cssClass="icon-margin" size="small" displayName={user} imageUrl={imageUrl} />
                    {user}
                </span>
            );
            const bySchedule = scheduleName && (
                <span className={flexCenterClass}>
                    &nbsp;{Resources.By}&nbsp;
                    '{scheduleName}'
                </span>
            );

            let content: JSX.Element | undefined = undefined;
            let label = ""
            switch (this.state.run.reason) {
                case BuildReason.BatchedCI:
                    content = <span>{Resources.ReasonBatchedCI}</span>;
                    label = Resources.ReasonBatchedCI;
                    break;
                case BuildReason.BuildCompletion:
                    content = <span className={flexCenterClass}>
                        <span>{Resources.ReasonBuildCompletion}</span>
                        {byElement}
                    </span>;
                    label = user ? format(Resources.ByFormat, Resources.ReasonBuildCompletion, user) : Resources.ReasonBuildCompletion;
                    break;
                case BuildReason.CheckInShelveset:
                    content = <span className={flexCenterClass}>
                        <span>{Resources.ReasonCheckInShelveset}</span>
                        {byElement}
                    </span>;
                    label = user ? format(Resources.ByFormat, Resources.ReasonCheckInShelveset, user) : Resources.ReasonCheckInShelveset;
                    break;
                case BuildReason.IndividualCI:
                    content = <span className={flexCenterClass}>
                        <span>{Resources.ReasonCI}</span>
                        {byElement}
                    </span>;
                    label = user ? format(Resources.ByFormat, Resources.ReasonCI, user) : Resources.ReasonCI;
                    break;
                case BuildReason.Manual:
                    content = <span className={flexCenterClass}>
                        <span>{Resources.ReasonManual}</span>
                        {byElement}
                    </span>;
                    label = user ? format(Resources.ByFormat, Resources.ReasonManual, user) : Resources.ReasonManual;
                    break;
                case BuildReason.PullRequest:
                    content = <span className={flexCenterClass}>
                        <span>{Resources.ReasonPullRequest}</span>
                        {byElement}
                    </span>;
                    label = user ? format(Resources.ByFormat, Resources.ReasonManual, user) : Resources.ReasonManual;
                    break;
                case BuildReason.Schedule:
                    content = <span className={flexCenterClass}>
                        <span>{Resources.ReasonScheduled}</span>
                        {bySchedule}
                    </span>;
                    label = scheduleName ? format(Resources.ByFormat, Resources.ReasonScheduled, scheduleName) : Resources.ReasonScheduled;
                    break;
                case BuildReason.ValidateShelveset:
                    content = <span className={flexCenterClass}>
                        <span>{Resources.ReasonValidate}</span>
                        {byElement}
                    </span>;
                    label = user ? format(Resources.ByFormat, Resources.ReasonValidate, user) : Resources.ReasonValidate;
                    break;
                case BuildReason.ResourceTrigger:
                    switch (run.triggerInfo[TriggerInfo.pipelineTriggerType]) {
                        case PipelineTriggerType.PipelineCompletion:
                            var aliasInfo = run.triggerInfo[TriggerInfo.alias];
                            var version = run.triggerInfo[TriggerInfo.version];
                            const pageContext = this.context.pageContext;
                            const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
                            const buildLink = linkingService.getBuildUrl(Number(run.triggerInfo[TriggerInfo.pipelineId]), "", run.triggerInfo[TriggerInfo.projectId]);
                            content = <span className={flexCenterClass}>
                                <span>{Resources.ReasonAutomaticTrigger}</span>
                                <span className={flexCenterClass}>
                                    &nbsp;{Resources.By}&nbsp;
                                    <Image className="icons-margin" src={getAssetLocation(this.context.pageContext, "ms.vss-build-web/artifacts-view/icons/pipeline.svg")} alt={Resources.Pipeline}/>
                                    <Link className="bolt-table-link bolt-table-inline-link text-ellipsis" href={buildLink} onClick={
                                        (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                                            event.preventDefault();
                                            onClickFPS(pageContext, buildLink, true, event);
                                        }
                                    }>
                                        {aliasInfo}/{version}
                                    </Link>
                                </span>
                            </span>;
                            break;

                        case PipelineTriggerType.BuildResourceCompletion:
                            var aliasInfo = run.triggerInfo[TriggerInfo.alias];
                            var source = run.triggerInfo[TriggerInfo.source];
                            var version = run.triggerInfo[TriggerInfo.version];
                            content = <span className={flexCenterClass}>
                                <span>{Resources.ReasonAutomaticTrigger}</span>
                                <span className={flexCenterClass} >
                                    &nbsp;{Resources.By}&nbsp;
                                    <Image className="icons-margin" src={getAssetLocation(this.context.pageContext, "ms.vss-build-web/artifacts-view/icons/jenkins.svg")} alt={Resources.Jenkins} />
                                    {aliasInfo}/{source}/{version}
                                </span>
                            </span>;
                            break;

                        case PipelineTriggerType.ContainerImage:
                            var aliasInfo = run.triggerInfo[TriggerInfo.alias];
                            var tag = run.triggerInfo[TriggerInfo.tag];
                            content = <span className={flexCenterClass}>
                                <span>{Resources.ReasonAutomaticTrigger}</span>
                                <span className={flexCenterClass}>
                                    &nbsp;{Resources.By}&nbsp;
                                    <Image className="icons-margin" src={getAssetLocation(this.context.pageContext, "ms.vss-build-web/artifacts-view/icons/azure-container-repository.svg")} alt={Resources.AzureContainerRepository} />
                                    {aliasInfo}/{tag}
                                </span>
                            </span>;
                            break;

                        case PipelineTriggerType.WebhookTriggeredEvent:
                            var aliasInfo = run.triggerInfo[TriggerInfo.alias];
                            content = <span className={flexCenterClass}>
                                <span>{Resources.ReasonAutomaticTrigger}</span>
                                <span className={flexCenterClass} >
                                    &nbsp;{Resources.By}&nbsp;
                                    <Image className="icons-margin" src={getAssetLocation(this.context.pageContext, "ms.vss-build-web/artifacts-view/icons/pipeline.svg")} alt={Resources.WebHook} />
                                    {aliasInfo}
                                </span>
                            </span>;
                            break;
                    }
                    label = user ? format(Resources.ByFormat, Resources.ReasonAutomaticTrigger, user) : Resources.ReasonAutomaticTrigger;
                    break; 
            }

            if (!!content) {
                return (
                    <div aria-label={label} className="flex-row">
                        <span className="flex-star flex-grow  body-xl">{content}</span>
                        {this._getChangesLinkComponent(run)}
                    </div>
                );
            }
        }

        return <div></div>;
    }

    private _onShowSummaryClick = (): void => {
        this._showSummary.value = !this._showSummary.value;
    };

    private _getAvatarUrl = (requestedFor: IdentityRef): string => {
        if (requestedFor) {
            if (requestedFor.imageUrl) {
                return requestedFor.imageUrl;
            }

            const avatarUrl = getAvatarUrl(requestedFor);
            if (avatarUrl) {
                return avatarUrl;
            }

            if (requestedFor.id) {
                return getIdentityImageUrl(this.context.pageContext, requestedFor.id);
            }
        }

        return "";
    }
}


